import { AxiosInstance } from "axios";
import { syncItemImageHashes } from "./itemImageCache";

const INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

type ApiProvider = () => AxiosInstance | null | undefined;

let timerId: ReturnType<typeof setInterval> | null = null;
let running = false;
let started = false;

/**
 * Background job: every 10 minutes, send cached image hashes to the server.
 * Matching hashes stay; mismatches receive replacement images.
 */
export function startItemImageSyncWorker(getApi: ApiProvider): () => void {
	if (started) {
		return stopItemImageSyncWorker;
	}
	started = true;

	const tick = async () => {
		if (running) return;
		if (typeof document !== "undefined" && document.hidden) return;

		const api = getApi();
		if (!api) return;

		running = true;
		try {
			const result = await syncItemImageHashes(api);
			if (result.updated > 0 && process.env.NODE_ENV !== "production") {
				// eslint-disable-next-line no-console
				console.info(
					`[itemImageSync] checked=${result.checked} updated=${result.updated} errors=${result.errors}`,
				);
			}
		} catch (err) {
			if (process.env.NODE_ENV !== "production") {
				// eslint-disable-next-line no-console
				console.warn("[itemImageSync] tick failed", err);
			}
		} finally {
			running = false;
		}
	};

	// First run after a short delay so login / catalog can settle
	const boot = window.setTimeout(() => {
		void tick();
	}, 15_000);

	timerId = setInterval(() => {
		void tick();
	}, INTERVAL_MS);

	return () => {
		window.clearTimeout(boot);
		stopItemImageSyncWorker();
	};
}

export function stopItemImageSyncWorker() {
	if (timerId != null) {
		clearInterval(timerId);
		timerId = null;
	}
	started = false;
	running = false;
}
